import { Popover as BasePopover } from "@base-ui/react/popover";
import type { ReactElement, ReactNode } from "react";

import { cn } from "../foundations/cn.js";
import { POPUP_MOTION } from "../foundations/interaction.js";

interface PopoverBaseProps {
  /** The button that opens it (a Button, an IconButton, a badge's button); it gets aria-expanded. */
  trigger: ReactElement;
  children: ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: "top" | "bottom" | "left" | "right";
  align?: "start" | "center" | "end";
  /** Opens under the pointer too, as a hover card would (a tap or the keyboard still opens it). */
  openOnHover?: boolean;
}

/** An explainer: a heading and a few short lines, padded and narrow. */
interface PopoverExplainerProps extends PopoverBaseProps {
  /** The heading, which names it as a dialog. */
  title: ReactNode;
  label?: never;
  className?: never;
}

/** A panel the content fills itself (a notification list): no heading, no padding, no width. */
interface PopoverPanelProps extends PopoverBaseProps {
  /** The panel's accessible name. */
  label: string;
  title?: never;
  /** The panel's width and padding. */
  className?: string;
}

export type PopoverProps = PopoverExplainerProps | PopoverPanelProps;

/**
 * A non-modal popup anchored to the button that opens it. With `title`, a few lines explaining
 * something (a rule behind a badge); with `label`, a panel worked in beside the page (the
 * notification list). Escape or a press outside closes it; focus moves in and returns to the
 * trigger.
 */
export function Popover({
  trigger,
  children,
  open,
  defaultOpen,
  onOpenChange,
  side = "bottom",
  align,
  openOnHover = false,
  ...shape
}: PopoverProps) {
  const explainer = shape.title !== undefined;
  return (
    <BasePopover.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={(next) => onOpenChange?.(next)}
    >
      <BasePopover.Trigger
        render={trigger}
        openOnHover={openOnHover}
        delay={openOnHover ? 150 : undefined}
      />
      <BasePopover.Portal>
        <BasePopover.Positioner
          side={side}
          align={align ?? (explainer ? "start" : "end")}
          sideOffset={explainer ? 6 : 8}
          className="z-50"
        >
          <BasePopover.Popup
            aria-label={explainer ? undefined : shape.label}
            className={cn(
              "border border-border-default bg-bg-raised shadow-float outline-none",
              POPUP_MOTION,
              explainer
                ? "flex max-w-80 flex-col gap-2 rounded-lg p-4 font-body text-body-b3 text-text-secondary"
                : "flex max-h-(--available-height) max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-2xl",
              explainer ? undefined : shape.className,
            )}
          >
            {explainer && (
              <BasePopover.Title className="font-body text-body-b2-strong text-text-primary">
                {shape.title}
              </BasePopover.Title>
            )}
            {children}
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}
